import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, Plus, Edit, Ban, RefreshCw, Trash2 } from 'lucide-react';
import CreateAccountModal from '../components/CreateAccountModal';
import EditAccountModal from '../components/EditAccountModal';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccountManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [searchTerm, users, roleFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [usersResponse, patientsResponse] = await Promise.all([
        axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/patients`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      
      // Merge patient IDs with user data
      const usersWithPatientIds = usersResponse.data.map(user => {
        if (user.role === 'patient') {
          const patientRecord = patientsResponse.data.find(p => p.user_id === user.id);
          if (patientRecord) {
            return { ...user, displayId: patientRecord.id };
          }
        }
        return { ...user, displayId: user.id };
      });
      
      setUsers(usersWithPatientIds);
      setFilteredUsers(usersWithPatientIds);
    } catch (error) {
      // console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };


  // Helper to get user's display name
  const getUserDisplayName = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.full_name || 'Unknown User';
  };

  const filterUsers = () => {
    let filtered = users.filter(user => {
      const searchLower = searchTerm.toLowerCase();
      const displayName = getUserDisplayName(user).toLowerCase();
      const matchesSearch = (
        displayName.includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower)
      );

      const matchesRole = roleFilter === 'all' || user.role === roleFilter;

      return matchesSearch && matchesRole;
    });

    setFilteredUsers(filtered);
  };

  const handleDisableUser = async (userId, currentStatus) => {
    // Default to 'active' if status is undefined
    const status = currentStatus || 'active';
    const action = status === 'active' ? 'DISABLE' : 'ENABLE';
    const newStatus = status === 'active' ? 'disabled' : 'active';
    
    const confirmMessage = status === 'active' 
      ? '⚠️ Are you sure you want to DISABLE this account?\n\nThe user will NOT be able to log in until you enable it again.'
      : '✅ Are you sure you want to ENABLE this account?\n\nThe user will be able to log in again.';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await axios.put(
        `${API}/admin/users/${userId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(`Account ${action.toLowerCase()}d successfully!`);
      fetchUsers();
    } catch (error) {
      // console.error('Error updating user status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleEditUser = async (user) => {
    if (user.role === 'patient') {
      // For patients, we need to find their patient record and redirect to patient edit page
      try {
        const response = await axios.get(`${API}/patients`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const patientRecord = response.data.find(p => p.user_id === user.id);
        
        if (patientRecord) {
          navigate(`/admin/patient/${patientRecord.id}/edit`);
        } else {
          toast.error('Patient record not found. Please use the Census page to manage patients.');
        }
      } catch (error) {
        // console.error('Error finding patient record:', error);
        toast.error('Unable to load patient record. Please use the Census page.');
      }
    } else {
      // For providers, admins, and employers, use the staff edit modal
      setEditingUser(user);
    }
  };

  const handleDeleteAccount = async (userId, userEmail) => {
    if (!window.confirm(`⚠️ Are you sure you want to DELETE this account?\n\nEmail: ${userEmail}\n\nThis action CANNOT be undone!`)) {
      return;
    }

    try {
      await axios.delete(
        `${API}/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Account deleted successfully');
      fetchUsers();
    } catch (error) {
      // console.error('Error deleting account:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    }
  };

  const handleResetPassword = async (userId, userEmail) => {
    if (!confirm(`Send password reset email to ${userEmail}?`)) {
      return;
    }

    try {
      await axios.post(
        `${API}/admin/users/${userId}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      alert('Password reset email sent successfully');
    } catch (error) {
      // console.error('Error sending reset email:', error);
      alert('Failed to send reset email');
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      patient: 'bg-blue-50 text-blue-700 border-blue-200',
      provider: 'bg-purple-50 text-purple-700 border-purple-200',
      admin: 'bg-orange-50 text-orange-700 border-orange-200',
      employer: 'bg-teal-50 text-teal-700 border-teal-200'
    };
    return <Badge className={`${styles[role] || ''} capitalize`}>{role}</Badge>;
  };

  const getStatusBadge = (status) => {
    return status === 'active' ? (
      <Badge className="badge-active">Active</Badge>
    ) : (
      <Badge className="badge-inactive">Disabled</Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-700"></div>
          <p className="mt-4 text-slate-600">Loading accounts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8" data-testid="account-management-header">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Account Management</h1>
          <p className="text-lg text-slate-600">Create and manage user accounts</p>
        </div>

        {/* Info Alert */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold mt-0.5">
              i
            </div>
            <div className="flex-1">
              <p className="text-sm text-blue-900">
                <strong>Note:</strong> This page is for creating and managing <strong>Provider</strong>, <strong>Admin</strong>, and <strong>Employer</strong> accounts. 
                To add new <strong>Patients</strong>, please go to the <a href="/admin/dashboard" className="underline font-semibold hover:text-blue-700">Patient Census Dashboard</a> and use the "Add Patient" button.
              </p>
            </div>
          </div>
        </div>

        <div className="card mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
            <div className="relative flex-1 w-full sm:max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input
                type="text"
                placeholder="Search by name or email..."
                data-testid="user-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                data-testid="role-filter-select"
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-all"
              >
                <option value="all">All Roles</option>
                <option value="patient">Patients</option>
                <option value="provider">Providers</option>
                <option value="admin">Admins</option>
                <option value="employer">Employers</option>
              </select>
              <Button
                onClick={() => setShowCreateModal(true)}
                data-testid="create-account-button"
                className="btn-primary flex items-center gap-2"
              >
                <Plus size={18} />
                Create Account
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-center py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider w-16">#</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Role</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Created</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-slate-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user, index) => (
                    <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-4 px-4 text-center text-slate-600 font-medium">
                        {index + 1}
                      </td>
                      <td className="py-4 px-4">
                        <p className="font-medium text-slate-900">{getUserDisplayName(user)}</p>
                        <p className="text-xs text-slate-500 font-mono">ID: {user.displayId}</p>
                      </td>
                      <td className="py-4 px-4 text-slate-700 text-sm">{user.email}</td>
                      <td className="py-4 px-4">{getRoleBadge(user.role)}</td>
                      <td className="py-4 px-4">{getStatusBadge(user.status || 'active')}</td>
                      <td className="py-4 px-4 text-slate-700 text-sm">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            onClick={() => handleEditUser(user)}
                            data-testid={`edit-user-${user.id}`}
                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"
                            size="sm"
                            title={user.role === 'patient' ? 'Edit patient demographics and membership' : 'Edit account details'}
                          >
                            <Edit size={14} className="mr-1" />
                            Edit
                          </Button>
                          <Button
                            onClick={() => handleResetPassword(user.id, user.email)}
                            data-testid={`reset-password-${user.id}`}
                            className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"
                            size="sm"
                          >
                            <RefreshCw size={14} className="mr-1" />
                            Reset
                          </Button>
                          <Button
                            onClick={() => handleDisableUser(user.id, user.status || 'active')}
                            data-testid={`disable-user-${user.id}`}
                            className="bg-red-600 hover:bg-red-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"
                            size="sm"
                          >
                            <Ban size={14} className="mr-1" />
                            {user.status === 'active' ? 'Disable' : 'Enable'}
                          </Button>
                          <Button
                            onClick={() => handleDeleteAccount(user.id, user.email)}
                            data-testid={`delete-user-${user.id}`}
                            className="bg-slate-800 hover:bg-slate-900 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"
                            size="sm"
                            title="Permanently delete this account"
                          >
                            <Trash2 size={14} className="mr-1" />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="card">
            <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-2">Total Users</h3>
            <p className="text-3xl font-bold text-slate-900">{users.length}</p>
          </div>
          <div className="card">
            <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-2">Patients</h3>
            <p className="text-3xl font-bold text-blue-600">
              {users.filter(u => u.role === 'patient').length}
            </p>
          </div>
          <div className="card">
            <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-2">Providers</h3>
            <p className="text-3xl font-bold text-purple-600">
              {users.filter(u => u.role === 'provider').length}
            </p>
          </div>
        </div>
      </div>

      <CreateAccountModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          fetchUsers();
          setShowCreateModal(false);
        }}
      />

      {editingUser && (
        <EditAccountModal
          open={!!editingUser}
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            fetchUsers();
            setEditingUser(null);
          }}
        />
      )}
    </div>
  );
};

export default AccountManagement;